import React, { useState, useMemo } from 'react';
import {
    Box,
    Grid,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Typography,
    InputAdornment,
    alpha,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';
import { BaseballRecruitProfile } from 'api/BaseballRecruits';
import BaseballCard from './BaseballCard';

interface BaseballCardGridProps {
    profiles: BaseballRecruitProfile[];
    onRefresh?: () => void;
}

type SortOption =
    | 'name'
    | 'gp_desc'
    | 'avg_desc'
    | 'h_desc'
    | 'hr_desc'
    | 'rbi_desc'
    | 'ops_desc'
    | 'era_asc'
    | 'k_desc'
    | 'whip_asc';
type SortLevel = 'all' | 'college' | 'summer' | 'hs';

function getBattingStat(profile: BaseballRecruitProfile, stat: string, level: SortLevel): number {
    const sources = [];
    if (level === 'college' || level === 'all') {
        const cd = profile.college_scraped_data;
        if (cd) sources.push(cd.career_totals?.batting || cd.seasons_data?.[0]?.batting);
    }
    if (level === 'summer' || level === 'all') {
        const sd = profile.summer_league_data;
        if (sd) sources.push(sd.career_totals?.batting || sd.seasons_data?.[0]?.batting);
    }
    if (level === 'hs' || level === 'all') {
        const d = profile.scraped_data;
        if (d) sources.push(d.career_totals?.batting || d.seasons_data?.[0]?.batting);
    }
    for (const batting of sources) {
        if (!batting) continue;
        const val = parseFloat(batting[stat] || '');
        if (!isNaN(val)) return val;
    }
    return -Infinity;
}

function getPitchingStat(profile: BaseballRecruitProfile, stat: string, level: SortLevel): number {
    const sources = [];
    if (level === 'college' || level === 'all') {
        const cd = profile.college_scraped_data;
        if (cd) sources.push(cd.career_totals?.pitching || cd.seasons_data?.[0]?.pitching);
    }
    if (level === 'summer' || level === 'all') {
        const sd = profile.summer_league_data;
        if (sd) sources.push(sd.career_totals?.pitching || sd.seasons_data?.[0]?.pitching);
    }
    if (level === 'hs' || level === 'all') {
        const d = profile.scraped_data;
        if (d) sources.push(d.career_totals?.pitching || d.seasons_data?.[0]?.pitching);
    }
    for (const pitching of sources) {
        if (!pitching) continue;
        const val = parseFloat(pitching[stat] || '');
        if (!isNaN(val)) return val;
    }
    return Infinity;
}

function hasAnyVisibleData(profile: BaseballRecruitProfile, visible: Set<string>): boolean {
    if (visible.has('college') && profile.college_scraped_data) return true;
    if (visible.has('summer') && profile.summer_league_data) return true;
    if (visible.has('hs') && profile.scraped_data) return true;
    return false;
}

const BaseballCardGrid: React.FC<BaseballCardGridProps> = ({ profiles }) => {
    const theme = useTheme();
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState<SortOption>('avg_desc');
    const [sortLevel, setSortLevel] = useState<SortLevel>('all');

    const hasCollege = profiles.some((p) => p.college_scraped_data);
    const hasSummer = profiles.some((p) => p.summer_league_data);
    const hasHs = profiles.some((p) => p.scraped_data);
    const dataLevelCount = [hasCollege, hasSummer, hasHs].filter(Boolean).length;

    const defaultVisible = useMemo(() => {
        const s = new Set<string>();
        if (hasCollege) s.add('college');
        if (hasSummer) s.add('summer');
        if (hasHs) s.add('hs');
        return s;
    }, [hasCollege, hasSummer, hasHs]);

    const [visibleLevels, setVisibleLevels] = useState<Set<string> | null>(null);
    const visible = visibleLevels ?? defaultVisible;

    const handleToggleLevel = (_: unknown, newLevels: string[]) => {
        if (newLevels.length === 0) return;
        setVisibleLevels(new Set(newLevels));
    };

    const filteredAndSorted = useMemo(() => {
        let result = profiles.filter((p) => hasAnyVisibleData(p, visible));

        if (search.trim()) {
            const term = search.toLowerCase();
            result = result.filter((p) => {
                const cd = p.college_scraped_data?.player;
                const name = p.individual?.name
                    ? p.individual.name
                    : cd?.first_name
                    ? `${cd.first_name} ${cd.last_name}`
                    : p.player_bio
                    ? `${p.player_bio.first_name} ${p.player_bio.last_name}`
                    : '';
                return name.toLowerCase().includes(term);
            });
        }

        result.sort((a, b) => {
            switch (sortBy) {
                case 'name': {
                    const getName = (p: BaseballRecruitProfile) =>
                        p.individual?.name ||
                        (p.college_scraped_data?.player
                            ? `${p.college_scraped_data.player.last_name} ${p.college_scraped_data.player.first_name}`
                            : p.player_bio
                            ? `${p.player_bio.last_name} ${p.player_bio.first_name}`
                            : '');
                    return getName(a).localeCompare(getName(b));
                }
                case 'gp_desc':
                    return getBattingStat(b, 'GP', sortLevel) - getBattingStat(a, 'GP', sortLevel);
                case 'avg_desc':
                    return (
                        getBattingStat(b, 'AVG', sortLevel) - getBattingStat(a, 'AVG', sortLevel)
                    );
                case 'h_desc':
                    return getBattingStat(b, 'H', sortLevel) - getBattingStat(a, 'H', sortLevel);
                case 'hr_desc':
                    return getBattingStat(b, 'HR', sortLevel) - getBattingStat(a, 'HR', sortLevel);
                case 'rbi_desc':
                    return (
                        getBattingStat(b, 'RBI', sortLevel) - getBattingStat(a, 'RBI', sortLevel)
                    );
                case 'ops_desc':
                    return (
                        getBattingStat(b, 'OPS', sortLevel) - getBattingStat(a, 'OPS', sortLevel)
                    );
                case 'k_desc':
                    return getPitchingStat(b, 'K', sortLevel) - getPitchingStat(a, 'K', sortLevel);
                case 'era_asc':
                    return (
                        getPitchingStat(a, 'ERA', sortLevel) - getPitchingStat(b, 'ERA', sortLevel)
                    );
                case 'whip_asc':
                    return (
                        getPitchingStat(a, 'WHIP', sortLevel) -
                        getPitchingStat(b, 'WHIP', sortLevel)
                    );
                default:
                    return 0;
            }
        });

        return result;
    }, [profiles, search, sortBy, sortLevel, visible]);

    const profilesWithoutData = profiles.filter(
        (p) => !p.scraped_data && !p.college_scraped_data && !p.summer_league_data,
    );

    const sortLevelLabel =
        sortLevel === 'college'
            ? 'College '
            : sortLevel === 'summer'
            ? 'Summer '
            : sortLevel === 'hs'
            ? 'HS '
            : '';

    return (
        <Box sx={{ p: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 3,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                }}
            >
                <TextField
                    size="small"
                    placeholder="Search players..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 200 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                {dataLevelCount >= 2 && (
                    <ToggleButtonGroup
                        size="small"
                        value={Array.from(visible)}
                        onChange={handleToggleLevel}
                    >
                        {hasCollege && (
                            <ToggleButton
                                value="college"
                                sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: alpha('#7b1fa2', 0.12),
                                        color: '#7b1fa2',
                                    },
                                }}
                            >
                                College
                            </ToggleButton>
                        )}
                        {hasSummer && (
                            <ToggleButton
                                value="summer"
                                sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: alpha('#00897b', 0.12),
                                        color: '#00897b',
                                    },
                                }}
                            >
                                Summer
                            </ToggleButton>
                        )}
                        {hasHs && (
                            <ToggleButton
                                value="hs"
                                sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: alpha('#1565c0', 0.12),
                                        color: '#1565c0',
                                    },
                                }}
                            >
                                HS
                            </ToggleButton>
                        )}
                    </ToggleButtonGroup>
                )}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                        value={sortBy}
                        label="Sort By"
                        onChange={(e) => setSortBy(e.target.value as SortOption)}
                    >
                        <MenuItem value="name">Name</MenuItem>
                        <MenuItem value="gp_desc">{sortLevelLabel}GP (High to Low)</MenuItem>
                        <MenuItem value="avg_desc">{sortLevelLabel}AVG (High to Low)</MenuItem>
                        <MenuItem value="h_desc">{sortLevelLabel}H (High to Low)</MenuItem>
                        <MenuItem value="hr_desc">{sortLevelLabel}HR (High to Low)</MenuItem>
                        <MenuItem value="rbi_desc">{sortLevelLabel}RBI (High to Low)</MenuItem>
                        <MenuItem value="ops_desc">{sortLevelLabel}OPS (High to Low)</MenuItem>
                        <MenuItem value="era_asc">{sortLevelLabel}ERA (Low to High)</MenuItem>
                        <MenuItem value="k_desc">{sortLevelLabel}K (High to Low)</MenuItem>
                        <MenuItem value="whip_asc">{sortLevelLabel}WHIP (Low to High)</MenuItem>
                    </Select>
                </FormControl>
                {dataLevelCount >= 2 && (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Sort Level</InputLabel>
                        <Select
                            value={sortLevel}
                            label="Sort Level"
                            onChange={(e) => setSortLevel(e.target.value as SortLevel)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            {hasCollege && <MenuItem value="college">College</MenuItem>}
                            {hasSummer && <MenuItem value="summer">Summer</MenuItem>}
                            {hasHs && <MenuItem value="hs">HS</MenuItem>}
                        </Select>
                    </FormControl>
                )}
                <Typography variant="body2" color="text.secondary">
                    {filteredAndSorted.length} player{filteredAndSorted.length !== 1 ? 's' : ''}
                </Typography>
            </Box>

            <Grid container spacing={2}>
                {filteredAndSorted.map((profile) => (
                    <Grid item xs={12} sm={6} md={4} lg={4} key={profile.uuid}>
                        <BaseballCard profile={profile} visibleLevels={visible} />
                    </Grid>
                ))}
            </Grid>

            {profilesWithoutData.length > 0 && (
                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.warning.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {profilesWithoutData.length} player
                        {profilesWithoutData.length !== 1 ? 's' : ''} pending data:{' '}
                        {profilesWithoutData.map((p) => p.individual?.name || 'Unknown').join(', ')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default BaseballCardGrid;
